import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
    Button,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Menu,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { cloneDeep, forEach, get, includes } from "lodash";
import React from "react";
import Dropzone from "react-dropzone";
import { Tooltip } from "react-tooltip";
import { CloudUpload, DownChevron, UpChevron } from "../../../common/js/icons";
import { acceptedFileTypes } from "../utils/constants";
import utils from "../utils/index";
import AdminDocumentTypeList from "./AdminDocumentTypeList";
import { docTypeIcons } from "./documentTypes";

const StyledPaperPadding = styled(Paper)({
    padding: "0 20px 20px",
    border: "none",
    marginBottom: "20px",
    boxShadow: "0 1px 2px rgba(34, 54, 70, 0.1)",
});

const StyledPaperHeading = styled("h3")({
    fontSize: "18px",
    margin: "14px 0",
});

const StyledHrDivider = styled("hr")({
    margin: "0 -20px",
    borderTop: "1px solid rgba(0, 0, 0, 0.06)",
});

const StyledUploadButton = styled(Button)({
    color: "#757575",
    fill: "#757575",
    "&:hover": {
        backgroundColor: "transparent",
    },
});

const StyledAttachmentCell = styled(TableCell)({
    fontSize: 14,
});

const StyledApprovedBy = styled("div")({
    fontWeight: 600,
});

const StyledSelect = styled("select")({
    fontSize: "14px",
    padding: "9px 6px 9px 6px",
    boxSizing: "border-box",
    height: "37px",
    width: "100%",
    "&:focus": {
        backgroundColor: "transparent",
        borderBottom: "none",
    },
    "&::before": {
        borderBottom: "none !important",
    },
    "&::after": {
        borderBottom: "none !important",
    },
});

const StyledMenuCategory = styled(MenuItem)({
    height: "12px",
    fontSize: "11px",
    fontStyle: "italic",
    textTransform: "uppercase",
});

const StyledDeleteProgress = styled("div")({
    textAlign: "center",
    width: "24px",
    display: "inline-block",
});

const StyledTable = styled(Table)({
    tableLayout: "fixed",
});

const StyledDocIcon = styled("div")({
    float: "left",
});

const StyledDocName = styled("div")({
    whiteSpace: "nowrap",
    overflow: "hidden",
    display: "block",
    textOverflow: "ellipsis",
    paddingTop: "2px",
    paddingLeft: "5px",
});

const StyledMenuItem = styled(MenuItem)({
    fontSize: "14px",
    padding: "0.5vh 2vw",
});

const DocumentList = (props) => {
    const {
        open,
        header,
        anchorEl,
        handleSort,
        docMenuKey,
        formDisabled,
        columnToSort,
        usedDocTypes,
        sortDirection,
        savedDocuments,
        handleMenuClose,
        toggleDeleteDialog,
        approveDocument,
        documentsUploading,
        selectDocumentType,
        handlePreviewDocument,
        handleOpenDocumentMenu,
        role,
        docApproved,
        restrictPreview,
        elementsSaving,
        alternateTitle,
        copyDocUrl,
        handleAddDocuments,
        options,
        toggleTerminationDoc,
        terminationDocument,
        type,
        voucherStatus,
        allDocumentTypes,
        noHeader,
    } = props;

    const handleSelectDocumentType = (key, event) => {
        selectDocumentType(key, event.target.value);
    };

    const handleRemoveDocument = (key, documentType) => {
        toggleDeleteDialog(true, key);

        if (documentType === "TERMINATION_DOCUMENT") {
            toggleTerminationDoc(false);
        }
    };

    const handleApproveDocument = (key) => {
        approveDocument(key);
    };

    const handleCopyDocumentUrl = (key) => {
        copyDocUrl(key);
        handleMenuClose();
    };

    let canView = [];
    let canDelete = [];
    let brokerPaymentDocTypes = cloneDeep(props.documentTypes);
    let documentTypes = cloneDeep(props.documentTypes);
    let disallowOtherOption = false;
    let voucherAwaitingPayment = voucherStatus === "Awaiting Payment" ? true : false;
    let voucherReadyForAccounting = voucherStatus === "Ready For Accounting" ? true : false;

    if (options) {
        canView = options.canView;
        canDelete = options.canDelete;
        if (get(options, "canUpload", []).length > 0 && documentTypes !== undefined) {
            forEach(Object.keys(documentTypes), (key) => {
                if (!includes(options.canUpload, key)) {
                    delete documentTypes[key];
                }
            });
        }
    }

    if (role === "broker") {
        forEach(Object.keys(documentTypes), (key) => {
            if (!includes(["OTHER", "COMMISSION_PAYMENT"], key)) {
                delete brokerPaymentDocTypes[key];
            }
        });
    }

    disallowOtherOption = get(options, "disallowOtherOption", false);
    let dropzoneRef;

    return (
        <StyledPaperPadding>
            <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                <Grid item>
                    <StyledPaperHeading>
                        {noHeader ? "" : alternateTitle ? alternateTitle : "Attached Documents"}
                    </StyledPaperHeading>
                </Grid>
                <Grid item>
                    <Dropzone
                        accept={acceptedFileTypes}
                        ref={(node) => {
                            dropzoneRef = node;
                        }}
                        onDrop={(accepted, rejected) => {
                            handleAddDocuments(accepted, rejected, type);
                        }}
                        style={{ display: "none" }}
                    >
                        {({ getRootProps, getInputProps }) => <input {...getInputProps()} />}
                    </Dropzone>
                    <StyledUploadButton
                        disabled={formDisabled}
                        component="span"
                        onClick={() => {
                            dropzoneRef.open();
                        }}
                    >
                        <CloudUpload />
                        &nbsp;&nbsp;Upload
                    </StyledUploadButton>
                </Grid>
            </Grid>
            <Grid container spacing={0}>
                <Grid item xs>
                    <StyledHrDivider />
                    <StyledTable>
                        <TableHead>
                            <TableRow>
                                {header.map((x, i) => (
                                    <TableCell onClick={() => handleSort(x.prop)} style={{ ...x.extraHeaderStyles }}>
                                        <span>{x.name}</span>
                                        {columnToSort === x.prop ? (
                                            sortDirection === "asc" ? (
                                                <UpChevron />
                                            ) : (
                                                <DownChevron />
                                            )
                                        ) : null}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {savedDocuments &&
                                Object.keys(savedDocuments).map((x, i) => {
                                    const document = savedDocuments[x];
                                    if (document["parentEntityType"] && props.parentEntityType) {
                                        if (document["parentEntityType"]["name"] !== props.parentEntityType) {
                                            return null;
                                        }
                                    }
                                    let removedDocumentType = false;
                                    if (
                                        document.mediaAssetType === "TERMINATION_DOCUMENT" &&
                                        get(terminationDocument, "upload", null) === false
                                    ) {
                                        toggleTerminationDoc(true);
                                    }
                                    if (documentTypes !== undefined) {
                                        removedDocumentType = !documentTypes.hasOwnProperty(
                                            get(document, "mediaAssetType")
                                        );
                                    }
                                    return (
                                        <TableRow key={`tr-doc-${i}`}>
                                            <StyledAttachmentCell
                                                style={{ width: "48%", overflow: "hidden", textOverflow: "ellipsis" }}
                                            >
                                                <StyledDocIcon>
                                                    {get(
                                                        docTypeIcons,
                                                        get(document, "mediaAssetName", " ")
                                                            .split(".")
                                                            .pop()
                                                            .toLowerCase(),
                                                        docTypeIcons["default"]
                                                    )()}
                                                </StyledDocIcon>
                                                <span data-tooltip-id={`doc-${i}`}>
                                                    <StyledDocName>{document["mediaAssetName"]}</StyledDocName>
                                                </span>
                                                <Tooltip id={`doc-${i}`} place="top" type="dark" effect="solid">
                                                    Uploaded on{" "}
                                                    {utils.operations.serializeLocalDateTimeForMediaAsset(
                                                        document.auditCreatedTimestamp
                                                    )}
                                                </Tooltip>
                                            </StyledAttachmentCell>
                                            <StyledAttachmentCell>
                                                <AdminDocumentTypeList
                                                    {...props}
                                                    stateCode={props.stateCode}
                                                    parentEntityType={props.parentEntityType}
                                                    preexistingValue={document}
                                                    style={{ display: props.adminListReady ? "" : "none" }}
                                                    mediaAssetKey={x}
                                                    documentTypesEnumList={allDocumentTypes}
                                                    updateDocumentFromAdminList={props.updateDocumentFromAdminList}
                                                    savedDocuments={savedDocuments}
                                                    voucherStatus={voucherStatus}
                                                    formDisabled={formDisabled}
                                                    options={options}
                                                    role={role}
                                                />
                                                {voucherAwaitingPayment &&
                                                role === "broker" &&
                                                includes(
                                                    ["OTHER", "COMMISSION_PAYMENT"],
                                                    document["mediaAssetType"]
                                                ) ? (
                                                    //adjusted document dropdown on vouchers for brokers to ONLY add commission payments when voucher is Awaiting Payment
                                                    <StyledSelect
                                                        disabled={
                                                            options
                                                                ? !includes(
                                                                      options.canView,
                                                                      document["mediaAssetType"]
                                                                  ) && document["updated"] === true
                                                                : formDisabled
                                                        }
                                                        name={`docType-select-${i}`}
                                                        value={
                                                            document && document["updated"] === true
                                                                ? document["mediaAssetType"]
                                                                : "select"
                                                        }
                                                        onChange={(e) => handleSelectDocumentType(x, e)}
                                                        style={{ display: props.adminListReady ? "none" : "" }}
                                                    >
                                                        {documentTypes &&
                                                            Object.keys(brokerPaymentDocTypes).map((r, q) => {
                                                                if (
                                                                    !usedDocTypes.includes(r) &&
                                                                    !brokerPaymentDocTypes[r]["required"] &&
                                                                    !brokerPaymentDocTypes[r]["multipleAllowed"]
                                                                ) {
                                                                    return (
                                                                        <StyledMenuCategory
                                                                            key={`doc-type-${i}-${q}`}
                                                                            value={r}
                                                                        >
                                                                            {brokerPaymentDocTypes[r]["display"]}
                                                                        </StyledMenuCategory>
                                                                    );
                                                                } else if (r === "OTHER" && disallowOtherOption) {
                                                                    return (
                                                                        <StyledMenuCategory
                                                                            key={`doc-type-required-${i}-${q}`}
                                                                            value={r}
                                                                            style={{ display: "none" }}
                                                                        >
                                                                            {brokerPaymentDocTypes[r]["display"]}
                                                                        </StyledMenuCategory>
                                                                    );
                                                                } else if (
                                                                    !brokerPaymentDocTypes[r]["required"] &&
                                                                    brokerPaymentDocTypes[r]["multipleAllowed"]
                                                                ) {
                                                                    return (
                                                                        <StyledMenuCategory
                                                                            key={`doc-type-${i}-${q}`}
                                                                            value={r}
                                                                        >
                                                                            {brokerPaymentDocTypes[r]["display"]}
                                                                        </StyledMenuCategory>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <StyledMenuCategory
                                                                            key={`doc-type-required-${i}-${q}`}
                                                                            value={r}
                                                                            style={{ display: "none" }}
                                                                        >
                                                                            {brokerPaymentDocTypes[r]["display"]}
                                                                        </StyledMenuCategory>
                                                                    );
                                                                }
                                                            })}
                                                    </StyledSelect>
                                                ) : (
                                                    <StyledSelect
                                                        disabled={
                                                            (voucherAwaitingPayment || voucherReadyForAccounting) &&
                                                            role === "broker"
                                                                ? true
                                                                : options && formDisabled
                                                                ? !(
                                                                      includes(
                                                                          options.canUpload,
                                                                          document["mediaAssetType"]
                                                                      ) && document["updated"] !== true
                                                                  )
                                                                : formDisabled
                                                        }
                                                        name={`docType-select-${i}`}
                                                        value={
                                                            document && document["updated"] === true
                                                                ? removedDocumentType
                                                                    ? allDocumentTypes[document["mediaAssetType"]]
                                                                    : document["mediaAssetType"]
                                                                : "select"
                                                        }
                                                        onChange={(e) => handleSelectDocumentType(x, e)}
                                                        style={{ display: props.adminListReady ? "none" : "" }}
                                                    >
                                                        <StyledMenuCategory value="" disabled>
                                                            Required
                                                        </StyledMenuCategory>
                                                        {documentTypes &&
                                                            Object.keys(documentTypes).map((j, k) => {
                                                                if (
                                                                    !usedDocTypes.includes(j) &&
                                                                    documentTypes[j]["required"] &&
                                                                    document["mediaAssetType"]
                                                                ) {
                                                                    return (
                                                                        <StyledMenuCategory
                                                                            key={`doc-type-required-${i}-${k}`}
                                                                            value={j}
                                                                        >
                                                                            {documentTypes[j]["display"]}
                                                                        </StyledMenuCategory>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <StyledMenuCategory
                                                                            key={`doc-type-required-${i}-${k}`}
                                                                            value={j}
                                                                            style={{ display: "none" }}
                                                                        >
                                                                            {documentTypes[j]["display"]}
                                                                        </StyledMenuCategory>
                                                                    );
                                                                }
                                                            })}
                                                        {documentTypes &&
                                                            Object.keys(documentTypes).map((j, k) => {
                                                                if (
                                                                    usedDocTypes.includes(j) &&
                                                                    documentTypes[j]["required"] &&
                                                                    documentTypes[j]["multipleAllowed"]
                                                                ) {
                                                                    return (
                                                                        <StyledMenuCategory
                                                                            key={`doc-type-required-${i}-${k}`}
                                                                            value={j}
                                                                        >
                                                                            {documentTypes[j]["display"]}
                                                                        </StyledMenuCategory>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <StyledMenuCategory
                                                                            key={`doc-type-required-${i}-${k}`}
                                                                            value={j}
                                                                            style={{ display: "none" }}
                                                                        >
                                                                            {documentTypes[j]["display"]}
                                                                        </StyledMenuCategory>
                                                                    );
                                                                }
                                                            })}
                                                        <Divider />
                                                        <StyledMenuCategory value="" disabled>
                                                            Optional
                                                        </StyledMenuCategory>
                                                        {/* THIS ALLOWS FOR DOCUMENT TYPE TO DISPLAY IF THE TYPE IS NO LONGER ALLOWED */}
                                                        {documentTypes && removedDocumentType ? (
                                                            <StyledMenuCategory
                                                                key={`doc-type-removed-1`}
                                                                value={allDocumentTypes[document["mediaAssetType"]]}
                                                            >
                                                                {allDocumentTypes[document["mediaAssetType"]]
                                                                    ? allDocumentTypes[document["mediaAssetType"]][
                                                                          "display"
                                                                      ]
                                                                    : "Other"}
                                                            </StyledMenuCategory>
                                                        ) : (
                                                            ""
                                                        )}
                                                        {documentTypes &&
                                                            Object.keys(documentTypes).map((r, q) => {
                                                                if (
                                                                    !usedDocTypes.includes(r) &&
                                                                    !documentTypes[r]["required"] &&
                                                                    !documentTypes[r]["multipleAllowed"]
                                                                ) {
                                                                    return (
                                                                        <StyledMenuCategory
                                                                            key={`doc-type-${i}-${q}`}
                                                                            value={r}
                                                                        >
                                                                            {documentTypes[r]["display"]}
                                                                        </StyledMenuCategory>
                                                                    );
                                                                } else if (r === "OTHER" && disallowOtherOption) {
                                                                    return (
                                                                        <StyledMenuCategory
                                                                            key={`doc-type-required-${i}-${q}`}
                                                                            value={r}
                                                                            style={{ display: "none" }}
                                                                        >
                                                                            {documentTypes[r]["display"]}
                                                                        </StyledMenuCategory>
                                                                    );
                                                                } else if (
                                                                    !documentTypes[r]["required"] &&
                                                                    documentTypes[r]["multipleAllowed"]
                                                                ) {
                                                                    return (
                                                                        <StyledMenuCategory
                                                                            key={`doc-type-${i}-${q}`}
                                                                            value={r}
                                                                        >
                                                                            {documentTypes[r]["display"]}
                                                                        </StyledMenuCategory>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <StyledMenuCategory
                                                                            key={`doc-type-required-${i}-${q}`}
                                                                            value={r}
                                                                            style={{ display: "none" }}
                                                                        >
                                                                            {documentTypes[r]["display"]}
                                                                        </StyledMenuCategory>
                                                                    );
                                                                }
                                                            })}
                                                    </StyledSelect>
                                                )}
                                            </StyledAttachmentCell>
                                            {role === "portlandBrokerAdmin" && (
                                                <TableCell>
                                                    <StyledApprovedBy>
                                                        {get(document, "approvedBy", "")}
                                                    </StyledApprovedBy>
                                                    <div>
                                                        {utils.operations.formatDate("MM-DD-YYYY")(
                                                            get(document, "approvedByTimestamp", "")
                                                        )}
                                                    </div>
                                                </TableCell>
                                            )}
                                            <TableCell
                                                style={{ minWidth: "24px", textAlign: "center", position: "relative" }}
                                            >
                                                {get(elementsSaving, `documents.${x}`) === true ? (
                                                    <StyledDeleteProgress>
                                                        <CircularProgress color="primary" size={20}></CircularProgress>
                                                    </StyledDeleteProgress>
                                                ) : (
                                                    <IconButton
                                                        style={{
                                                            position: "absolute",
                                                            top: "22%",
                                                            left: "28%",
                                                            padding: "5px",
                                                        }}
                                                        disabled={
                                                            restrictPreview
                                                                ? true
                                                                : options
                                                                ? includes(canView, document["mediaAssetType"]) ||
                                                                  includes(canDelete, document["mediaAssetType"])
                                                                    ? false
                                                                    : true
                                                                : false
                                                        }
                                                        aria-label="More"
                                                        aria-owns={
                                                            Boolean(document["anchorEl"]) ? `long-menu-${x}-${i}` : null
                                                        }
                                                        aria-haspopup="true"
                                                        onClick={(e) => handleOpenDocumentMenu(x, e.currentTarget)}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                )}
                                                <Menu
                                                    id={`long-menu-${x}-${i}`}
                                                    anchorEl={anchorEl}
                                                    open={open}
                                                    onClose={handleMenuClose}
                                                >
                                                    {(!options ||
                                                        includes(
                                                            canView,
                                                            get(savedDocuments, `${docMenuKey}.mediaAssetType`)
                                                        )) && (
                                                        <StyledMenuItem
                                                            onClick={() =>
                                                                handlePreviewDocument(
                                                                    get(document, "url", null),
                                                                    docMenuKey
                                                                )
                                                            }
                                                        >
                                                            Open in a new tab
                                                        </StyledMenuItem>
                                                    )}
                                                    {(!options ||
                                                        includes(
                                                            canView,
                                                            get(savedDocuments, `${docMenuKey}.mediaAssetType`)
                                                        )) && (
                                                        <StyledMenuItem
                                                            onClick={() => handleCopyDocumentUrl(docMenuKey)}
                                                        >
                                                            Copy Link
                                                        </StyledMenuItem>
                                                    )}
                                                    {role === "portlandBrokerAdmin" && (
                                                        <StyledMenuItem
                                                            id={`doc-approve-${x}-${i}`}
                                                            disabled={docApproved}
                                                            onClick={() => handleApproveDocument(docMenuKey)}
                                                        >
                                                            Approve
                                                        </StyledMenuItem>
                                                    )}
                                                    {(!options ||
                                                        includes(
                                                            canDelete,
                                                            get(savedDocuments, `${docMenuKey}.mediaAssetType`)
                                                        )) && (
                                                        <StyledMenuItem
                                                            onClick={() =>
                                                                handleRemoveDocument(
                                                                    docMenuKey,
                                                                    get(savedDocuments, `${docMenuKey}.mediaAssetType`)
                                                                )
                                                            }
                                                        >
                                                            Delete
                                                        </StyledMenuItem>
                                                    )}
                                                </Menu>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}

                            {documentsUploading
                                ? Object.keys(documentsUploading).map((x) => {
                                      if (get(documentsUploading, x)) {
                                          return (
                                              <React.Fragment>
                                                  <TableRow style={{ height: "30px" }}>
                                                      <TableCell
                                                          colSpan={5}
                                                          style={{
                                                              paddingBottom: "0px",
                                                              borderBottom: "none",
                                                              position: "relative",
                                                          }}
                                                      >
                                                          <div style={{ position: "absolute" }}>{x}</div>
                                                      </TableCell>
                                                  </TableRow>
                                                  <TableRow style={{ height: "30px" }}>
                                                      <TableCell
                                                          colSpan={5}
                                                          style={{ paddingTop: "0px" }}
                                                          align="right"
                                                      >
                                                          <div
                                                              style={{
                                                                  display: "flex",
                                                                  paddingRight: "2vw",
                                                                  justifyContent: "end",
                                                              }}
                                                          >
                                                              <LinearProgress style={{ width: "60%" }} />
                                                          </div>
                                                      </TableCell>
                                                  </TableRow>
                                              </React.Fragment>
                                          );
                                      } else {
                                          return null;
                                      }
                                  })
                                : null}
                        </TableBody>
                    </StyledTable>
                </Grid>
            </Grid>
        </StyledPaperPadding>
    );
};

export default DocumentList;
